import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import ZoomControls from '../ZoomControls';

/**
 * 🎨 PaintingCanvas2 - Canvas de Pintura Baseado no SimplePaint Funcional
 * 
 * Implementação do zero baseada no código funcional de /paint/
 * Usa o padrão correto de eventos dinâmicos do SimplePaint
 */
const PaintingCanvas2 = ({ isActive }) => {
  const canvasContainerRef = useRef(null);
  const croquisRef = useRef(null);
  const brushRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showErasingIndicator, setShowErasingIndicator] = useState(false);
  const zoomRef = useRef(1);
  const currentToolRef = useRef('pen');
  const [paperPosition, setPaperPosition] = useState({
    x: 0,
    y: 0,
    scale: 1,
    width: 0,
    height: 0
  });
  
  const {
    selectedImageId,
    uploadedImages,
    paintingSettings,
    updateImage,
  } = useApp();
  
  const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
  const selectedImageIndex = uploadedImages.findIndex(img => img.id === selectedImageId);
  
  // Settings da pintura (vindos da sidebar)
  const currentTool = paintingSettings.currentTool || 'pen';
  const brushSize = paintingSettings.brushSize || 20;
  const brushColor = paintingSettings.brushColor || '#000000';
  const brushOpacity = paintingSettings.brushOpacity || 100;
  const brushFlow = paintingSettings.brushFlow || 6;
  const brushSpacing = paintingSettings.brushSpacing || 2;
  const stabilizerLevel = paintingSettings.stabilizerLevel || 10;
  const stabilizerWeight = paintingSettings.stabilizerWeight || 50;
  const currentBrushImage = paintingSettings.currentBrushImage || null;
  const blendMode = paintingSettings.blendMode || 'source-over';
  
  // Sincronizar zoom
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  
  // Sincronizar currentTool
  useEffect(() => {
    currentToolRef.current = currentTool;
  }, [currentTool]);

  // 🎨 Função para atualizar thumbnail da camada
  const updateThumbnail = () => {
    if (!croquisRef.current || !selectedImage || !selectedImageId) {
      return;
    }

    try {
      const croquis = croquisRef.current;
      const layers = croquis.getLayers();

      if (!layers || layers.length < 2) {
        return;
      }

      const workLayer = layers[1];
      if (!workLayer?.children?.[0]) {
        return;
      }

      // Criar thumbnail pequeno (mesmo tamanho da imagem original)
      const thumbnailCanvas = document.createElement('canvas');
      thumbnailCanvas.width = selectedImage.image.width;
      thumbnailCanvas.height = selectedImage.image.height;
      const ctx = thumbnailCanvas.getContext('2d');

      // Desenhar camada de trabalho
      ctx.drawImage(workLayer.children[0], 0, 0);

      // Converter para data URL
      const thumbnailUrl = thumbnailCanvas.toDataURL('image/png');

      // Criar nova Image e atualizar
      const thumbnailImg = new Image();
      thumbnailImg.onload = () => {
        // Atualizar apenas o thumbnail (sem triggerar re-render do Croquis)
        updateImage(selectedImageId, {
          image: thumbnailImg,
          src: thumbnailUrl,
          thumbnailUpdate: Date.now(), // Timestamp para forçar atualização visual
        });
      };
      thumbnailImg.src = thumbnailUrl;

      console.log('🖼️ [PaintingCanvas2] Thumbnail atualizado');
    } catch (error) {
      console.error('❌ [PaintingCanvas2] Erro ao atualizar thumbnail:', error);
    }
  };

  // Calcular posição do papel (baseado no FluidCanvas)
  useEffect(() => {
    if (!isActive) return;
    
    // Usar a primeira imagem como referência para dimensões
    const firstImage = uploadedImages[0];
    if (!firstImage || !firstImage.image) return;

    const handleResize = () => {
      const container = canvasContainerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // No mobile, ajustar escala para fit-to-screen
      // No desktop, usar tamanho real (1:1)
      let autoScale = 1;

      const paperWidth = firstImage.image.width;
      const paperHeight = firstImage.image.height;
      
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile && paperWidth > 0 && paperHeight > 0) {
        const margin = 20;
        const scaleX = (containerWidth - margin * 2) / paperWidth;
        const scaleY = (containerHeight - margin * 2) / paperHeight;
        autoScale = Math.min(scaleX, scaleY, 1);
      }

      let scaledPaperWidth = 0;
      let scaledPaperHeight = 0;
      let paperX = containerWidth / 2;
      let paperY = containerHeight / 2;

      if (paperWidth > 0 && paperHeight > 0) {
        scaledPaperWidth = paperWidth * autoScale;
        scaledPaperHeight = paperHeight * autoScale;

        const isDesktop = !isMobile;
        const sidebarWidth = 320;

        if (isDesktop) {
          const availableWidth = containerWidth - sidebarWidth;
          paperX = sidebarWidth + (availableWidth - scaledPaperWidth) / 2;
        } else {
          paperX = (containerWidth - scaledPaperWidth) / 2;
        }

        paperY = (containerHeight - scaledPaperHeight) / 2;
      }

      setPaperPosition({
        x: paperX,
        y: paperY,
        scale: autoScale,
        width: scaledPaperWidth,
        height: scaledPaperHeight
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isActive, uploadedImages]);

  // ✅ Inicializar Croquis (padrão SimplePaint)
  useEffect(() => {
    if (!isActive || !selectedImage || !window.Croquis || !canvasContainerRef.current) {
      return;
    }

    console.log('🎨 [PaintingCanvas2] Inicializando Croquis...');

    // Limpar instância anterior
    if (croquisRef.current) {
      try {
        const element = croquisRef.current.getDOMElement();
        if (element?.parentNode) {
          element.parentNode.removeChild(element);
        }
        croquisRef.current = null;
      } catch (e) {
        console.error('Erro ao limpar canvas anterior:', e);
      }
    }

    // Criar Croquis
    const croquis = new window.Croquis();
    croquisRef.current = croquis;

    // Criar pincel
    const brush = new window.Croquis.Brush();
    brush.setSize(brushSize);
    brush.setColor(brushColor);
    brush.setSpacing(brushSpacing / 100);
    brush.setFlow(brushFlow / 100);
    brushRef.current = brush;

    croquis.setTool(brush);
    croquis.setToolStabilizeLevel(stabilizerLevel);
    croquis.setToolStabilizeWeight(stabilizerWeight / 100);
    croquis.setPaintingOpacity(brushOpacity / 100);
    croquis.setPaintingKnockout(currentToolRef.current === 'eraser');

    // Configurar canvas
    croquis.lockHistory();
    croquis.setCanvasSize(selectedImage.image.width, selectedImage.image.height);

    // ✅ ORDEM: FUNDO → TRABALHO → FRENTE
    // Camada 0 - Fundo
    croquis.addLayer();
    const bgCanvas = croquis.getLayers()[0].children[0];
    const bgCtx = bgCanvas.getContext('2d');
    bgCtx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);
    
    // Removido: baseLayer não existe mais no MVP
    // A imagem de fundo será a própria imagem

    // Desenhar camadas ABAIXO
    for (let i = 0; i < selectedImageIndex; i++) {
      const layer = uploadedImages[i];
      if (layer.isVisible !== false && layer.image) {
        bgCtx.globalAlpha = (layer.opacity || 100) / 100;
        bgCtx.globalCompositeOperation = layer.blendMode || 'source-over';
        bgCtx.drawImage(layer.image, 0, 0);
      }
    }
    bgCtx.globalAlpha = 1;
    bgCtx.globalCompositeOperation = 'source-over';

    // Camada 1 - Trabalho (imagem selecionada)
    croquis.addLayer();
    const workingCanvas = croquis.getLayers()[1].children[0];
    const ctx = workingCanvas.getContext('2d');
    ctx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);
    ctx.globalAlpha = (selectedImage.opacity || 100) / 100;
    ctx.globalCompositeOperation = selectedImage.blendMode || 'source-over';
    ctx.drawImage(selectedImage.image, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // Camada 2 - Frente (camadas acima)
    croquis.addLayer();
    const frontCanvas = croquis.getLayers()[2].children[0];
    const frontCtx = frontCanvas.getContext('2d');
    frontCtx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);

    // Desenhar camadas ACIMA (índices MAIORES que selectedImageIndex)
    for (let i = selectedImageIndex + 1; i < uploadedImages.length; i++) {
      const layer = uploadedImages[i];
      if (layer.isVisible !== false && layer.image) {
        frontCtx.globalAlpha = (layer.opacity || 100) / 100;
        frontCtx.globalCompositeOperation = layer.blendMode || 'source-over';
        frontCtx.drawImage(layer.image, 0, 0);
      }
    }
    frontCtx.globalAlpha = 1;
    frontCtx.globalCompositeOperation = 'source-over';

    // Selecionar camada de TRABALHO para pintar/apagar (onde a imagem está)
    croquis.selectLayer(1);
    croquis.setUndoLimit(100);
    croquis.unlockHistory();
    
    console.log('🎨 [PaintingCanvas2] Camadas criadas:', {
      total: croquis.getLayers().length,
      selected: 1,
      layers: {
        0: 'Fundo (camadas abaixo)',
        1: 'Trabalho (pinta/apaga aqui)',
        2: 'Frente (camadas acima)'
      }
    });

    // Adicionar ao DOM
    const croquisElement = croquis.getDOMElement();
    croquisElement.style.position = 'absolute';
    croquisElement.style.display = 'block';
    croquisElement.style.transform = `scale(${zoomRef.current})`;
    croquisElement.style.transformOrigin = 'top left';
    
    // Posicionamento inicial será feito pelo useEffect de posicionamento
    canvasContainerRef.current.appendChild(croquisElement);

    // ✅ FUNÇÕES AUXILIARES (copiadas do SimplePaint funcional)
    
    // Simular pressão de caneta/tablet
    const setPointerEvent = (e) => {
      // Verificar se Table t está disponível e se pen() retorna dados válidos
      if (e.pointerType !== 'pen' && window.Croquis && window.Croquis.Tablet) {
        try {
          const penData = window.Croquis.Tablet.pen();
          if (penData && penData.pointerType) {
            e.pointerType = 'pen';
            e.pressure = window.Croquis.Tablet.pressure();
            if (window.Croquis.Tablet.isEraser && window.Croquis.Tablet.isEraser()) {
              Object.defineProperties(e, {
                'button': { value: 5 },
                'buttons': { value: 32 }
              });
            }
          }
        } catch (err) {
          // Tablet não disponível ou erro - ignorar silenciosamente
        }
      }
    };

    // Variável global para posição do mouse (padrão SimplePaint)
    const mouse = {
      x: 0, y: 0,
      rx: 0, ry: 0
    };
    
    // Calcular posição relativa (com zoom usando ref)
    const getRelativePosition = () => {
      const canvas = croquisElement.querySelector('canvas');
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      mouse.rx = (mouse.x - rect.left) * (1 / zoomRef.current);
      mouse.ry = (mouse.y - rect.top) * (1 / zoomRef.current);
    };

    // ✅ EVENT HANDLERS (padrão SimplePaint - eventos dinâmicos)
    
    const handleCanvasPointerDown = (e) => {
      const tool = currentToolRef.current;
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      setPointerEvent(e);
      getRelativePosition();
      
      // Determinar se é borracha
      const isErasing = tool === 'eraser' || (tool === 'pen' && e.pointerType === 'pen' && e.button == 5);
      croquis.setPaintingKnockout(isErasing);
      setShowErasingIndicator(isErasing);
      
      console.log('🎨 [PaintingCanvas2] Pointer down:', { 
        tool, 
        isErasing, 
        currentLayer: croquis.getCurrentLayerIndex(),
        pos: { x: mouse.rx, y: mouse.ry }
      });
      
      if (tool !== 'picker') {
        croquis.down(mouse.rx, mouse.ry, e.pointerType === 'pen' ? e.pressure : 1);
      }
      
      // ✅ Adicionar listeners dinamicamente (padrão SimplePaint)
      if (tool !== 'line') {
        document.addEventListener('pointermove', handleCanvasPointerMove);
      }
      document.addEventListener('pointerup', handleCanvasPointerUp);
    };
    
    const handleCanvasPointerMove = (e) => {
      const tool = currentToolRef.current;
      setPointerEvent(e);
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      getRelativePosition();
      
      if (tool !== 'picker') {
        const isErasing = tool === 'eraser';
        console.log('🎨 [PaintingCanvas2] Move:', { tool, isErasing, pos: { x: mouse.rx, y: mouse.ry } });
        croquis.move(mouse.rx, mouse.ry, e.pointerType === 'pen' ? e.pressure : 1);
      }
    };
    
    const handleCanvasPointerUp = (e) => {
      const tool = currentToolRef.current;
      setPointerEvent(e);
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      getRelativePosition();

      if (tool !== 'picker') {
        const isErasing = tool === 'eraser';
        console.log('🎨 [PaintingCanvas2] Up:', { tool, isErasing, pos: { x: mouse.rx, y: mouse.ry } });
        croquis.up(mouse.rx, mouse.ry, e.pointerType === 'pen' ? e.pressure : 1);

        // ✅ Atualizar thumbnail após pintar
        setTimeout(() => {
          updateThumbnail();
        }, 50); // Pequeno delay para garantir que o canvas foi atualizado
      }

      // ✅ Remover listeners (padrão SimplePaint)
      document.removeEventListener('pointermove', handleCanvasPointerMove);
      document.removeEventListener('pointerup', handleCanvasPointerUp);

      setShowErasingIndicator(false);
    };

    // ✅ Registrar APENAS pointerdown (padrão SimplePaint)
    croquisElement.addEventListener('pointerdown', handleCanvasPointerDown);

    setIsInitialized(true);

    // Cleanup
    return () => {
      croquisElement.removeEventListener('pointerdown', handleCanvasPointerDown);
      document.removeEventListener('pointermove', handleCanvasPointerMove);
      document.removeEventListener('pointerup', handleCanvasPointerUp);
    };
  }, [isActive, selectedImage?.id]);

  // 🎨 Expor handlers de undo/redo para o sistema de teclado
  useEffect(() => {
    if (!croquisRef.current || !isActive) {
      // Limpar handlers ao desativar
      delete window.__painting2UndoHandler;
      delete window.__painting2RedoHandler;
      delete window.__painting2CanUndo;
      delete window.__painting2CanRedo;
      return;
    }

    console.log('🎨 [PaintingCanvas2] Registrando handlers de undo/redo');

    // Registrar handlers customizados para Pintura2
    window.__painting2UndoHandler = () => {
      if (croquisRef.current) {
        croquisRef.current.undo();
        console.log('↩️ [PaintingCanvas2] Undo executado');
        updateThumbnail();
      }
    };

    window.__painting2RedoHandler = () => {
      if (croquisRef.current) {
        croquisRef.current.redo();
        console.log('↪️ [PaintingCanvas2] Redo executado');
        updateThumbnail();
      }
    };

    window.__painting2CanUndo = () => {
      // Croquis sempre permite undo (internamente verifica se há histórico)
      return !!croquisRef.current;
    };

    window.__painting2CanRedo = () => {
      // Croquis sempre permite redo (internamente verifica se há histórico)
      return !!croquisRef.current;
    };

    return () => {
      delete window.__painting2UndoHandler;
      delete window.__painting2RedoHandler;
      delete window.__painting2CanUndo;
      delete window.__painting2CanRedo;
    };
  }, [isActive, isInitialized]);

  // 🎨 Cleanup ao desmontar - salvar estado final
  // IMPORTANTE: Não incluir saveCurrentState nas dependências para evitar loop
  useEffect(() => {
    return () => {
      // Salvar apenas quando realmente está saindo (isActive vai para false)
      if (croquisRef.current && selectedImage && selectedImageId) {
        console.log('🎨 [PaintingCanvas2] Componente desmontando, salvando estado final...');

        try {
          const croquis = croquisRef.current;
          const layers = croquis.getLayers();

          if (!layers || layers.length < 2) {
            return;
          }

          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = selectedImage.image.width;
          tempCanvas.height = selectedImage.image.height;
          const tempCtx = tempCanvas.getContext('2d');

          const workLayer = layers[1];
          if (workLayer?.children?.[0]) {
            tempCtx.drawImage(workLayer.children[0], 0, 0);
          }

          tempCanvas.toBlob((blob) => {
            if (blob) {
              const resultUrl = URL.createObjectURL(blob);
              const newImg = new Image();

              newImg.onload = () => {
                updateImage(selectedImageId, {
                  image: newImg,
                  src: resultUrl,
                  // NÃO atualizar lastEdited aqui para evitar loop
                });

                if (selectedImage.src?.startsWith('blob:')) {
                  URL.revokeObjectURL(selectedImage.src);
                }
              };

              newImg.onerror = () => {
                URL.revokeObjectURL(resultUrl);
              };

              newImg.src = resultUrl;
            }
          }, 'image/png');
        } catch (error) {
          console.error('❌ [PaintingCanvas2] Erro ao salvar estado:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vazio - executa cleanup apenas ao desmontar

  // Atualizar propriedades do pincel
  useEffect(() => {
    if (!brushRef.current || !croquisRef.current || !isInitialized) return;
    console.log('🎨 [PaintingCanvas2] Atualizando pincel:', { brushSize, brushColor, currentTool });
    brushRef.current.setSize(brushSize);
    brushRef.current.setColor(brushColor);
    brushRef.current.setSpacing(brushSpacing / 100);
    brushRef.current.setFlow(brushFlow / 100);
    croquisRef.current.setPaintingOpacity(brushOpacity / 100);
    croquisRef.current.setToolStabilizeLevel(stabilizerLevel);
    croquisRef.current.setToolStabilizeWeight(stabilizerWeight / 100);
  }, [brushSize, brushColor, brushOpacity, brushFlow, brushSpacing, stabilizerLevel, stabilizerWeight, isInitialized]);

  // Aplicar zoom quando mudar
  useEffect(() => {
    if (!croquisRef.current || !isInitialized) return;
    
    const croquisElement = croquisRef.current.getDOMElement();
    if (croquisElement) {
      croquisElement.style.transform = `scale(${zoom})`;
      croquisElement.style.transformOrigin = 'top left';
    }
  }, [zoom, isInitialized]);

  // Alternar ferramenta (atualizar setPaintingKnockout)
  useEffect(() => {
    if (!croquisRef.current || !isInitialized) return;
    console.log('🎨 [PaintingCanvas2] Alternando ferramenta:', { tool: currentToolRef.current });
    croquisRef.current.setPaintingKnockout(currentToolRef.current === 'eraser');
  }, [currentTool, isInitialized]);

  // Atualizar imagem do pincel
  useEffect(() => {
    if (!brushRef.current || !isInitialized) return;
    
    if (currentBrushImage) {
      const img = new Image();
      img.onload = () => {
        if (brushRef.current) {
          brushRef.current.setImage(img);
          console.log('🎨 [PaintingCanvas2] Imagem do pincel carregada:', currentBrushImage);
        }
      };
      img.onerror = () => {
        console.error('❌ Erro ao carregar pincel:', currentBrushImage);
      };
      img.src = currentBrushImage;
    } else {
      // Resetar para pincel circular padrão
      if (brushRef.current) {
        brushRef.current.setImage(null);
        console.log('🎨 [PaintingCanvas2] Resetando para pincel circular padrão');
      }
    }
  }, [currentBrushImage, isInitialized]);

  // Atualizar blend mode
  useEffect(() => {
    if (!croquisRef.current || !isInitialized || currentTool === 'eraser') return;
    croquisRef.current.setPaintingBlendMode(blendMode);
    console.log('🎨 [PaintingCanvas2] Blend mode atualizado:', blendMode);
  }, [blendMode, isInitialized, currentTool]);

  // Atualizar posição e escala do canvas quando mudarem
  useEffect(() => {
    if (!croquisRef.current || !isInitialized || !selectedImage || !paperPosition) return;
    
    const croquisElement = croquisRef.current.getDOMElement();
    if (!croquisElement) return;
    
    // Calcular posição da imagem baseada em transform e paperPosition
    const imageX = paperPosition.x + (selectedImage.transform.x * paperPosition.scale);
    const imageY = paperPosition.y + (selectedImage.transform.y * paperPosition.scale);
    const imageWidth = selectedImage.transform.width * paperPosition.scale;
    const imageHeight = selectedImage.transform.height * paperPosition.scale;
    
    // Posicionar o canvas absolutamente
    croquisElement.style.position = 'absolute';
    croquisElement.style.left = `${imageX}px`;
    croquisElement.style.top = `${imageY}px`;
    croquisElement.style.width = `${imageWidth}px`;
    croquisElement.style.height = `${imageHeight}px`;
    
    console.log('🎨 [PaintingCanvas2] ⚠️ Canvas reposicionado:', { imageX, imageY, imageWidth, imageHeight, zoom: zoomRef.current });
  }, [isInitialized, selectedImage?.id, selectedImage?.transform?.x, selectedImage?.transform?.y, selectedImage?.transform?.width, selectedImage?.transform?.height, paperPosition.x, paperPosition.y, paperPosition.scale, paperPosition.width, paperPosition.height]);

  if (!isActive || !selectedImage) {
    return null;
  }

  return (
    <div className="h-full w-full flex" style={{ backgroundColor: '#252722' }}>
      {/* Canvas Container */}
      <div 
        ref={canvasContainerRef}
        className="flex-1 relative overflow-auto"
        style={{
          background: `
            linear-gradient(45deg, rgba(79, 113, 11, 0.15) 25%, transparent 25%),
            linear-gradient(-45deg, rgba(79, 113, 11, 0.15) 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, rgba(79, 113, 11, 0.15) 75%),
            linear-gradient(-45deg, transparent 75%, rgba(79, 113, 11, 0.15) 75%)
          `,
          backgroundColor: '#252722',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
        }}
      >
        {/* Indicador de Borracha */}
        {showErasingIndicator && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-pulse">
            🗑️ Apagando...
          </div>
        )}
      </div>

      {/* Controles de Zoom */}
      <div className="absolute bottom-4 right-4 z-50">
        <ZoomControls zoom={zoom} onZoomChange={setZoom} />
      </div>
    </div>
  );
};

export default PaintingCanvas2;
